export interface DiscountRuleSelector {
    type: string;
    value: string;
    [key: string]: unknown;
}

export interface DiscountRule {
    uuid: string;
    name: string;
    type?: string;
    buy_quantity: number;
    discount_quantity: number;
    discount_percent?: number;
    min_order_total?: number | null;
    selector_match: 'all' | 'any';
    selectors: DiscountRuleSelector[];
    [key: string]: unknown;
}

export interface QuoteLineItem {
    uuid: string;
    name?: string;
    sku?: string;
    type?: string;
    tax_class?: string;
    price: number;
    quantity: number;
    [key: string]: unknown;
}

export interface DisplayItem extends QuoteLineItem {
    discountQty: number;
    discountPercent: number;
    paidQty: number;
}

export interface RuleDiscountItem {
    uuid: string;
    name?: string;
    sku?: string;
    quantity: number;
    unitPrice: number;
    discountPercent: number;
}

export interface RuleDiscount {
    rule: DiscountRule;
    discountItems: RuleDiscountItem[];
    savingAmount: number;
}

export interface ApplyDiscountRulesResult {
    displayItems: DisplayItem[];
    ruleDiscounts: RuleDiscount[];
}

const matchesSelector = (item: QuoteLineItem, selectorType: string, selectorValue: string): boolean => {
    if (selectorType === 'sku_prefix') {
        return !!item.sku?.startsWith(selectorValue);
    }

    if (selectorType === 'product_type') {
        return item.type === selectorValue;
    }

    return false;
};

const itemMatchesRule = (item: QuoteLineItem, rule: DiscountRule): boolean => {
    const results = rule.selectors.map((s) => {
        return matchesSelector(item, s.type, s.value);
    });

    return rule.selector_match === 'all'
        ? results.every(Boolean)
        : results.some(Boolean);
};

export const applyDiscountRules = (items: QuoteLineItem[], allRules: DiscountRule[] | null | undefined): ApplyDiscountRulesResult => {
    const rules = (allRules ?? []).filter((rule) => {
        return (rule.type ?? 'buy_x_get_y') === 'buy_x_get_y';
    });

    if (rules.length === 0) {
        return {
            displayItems: items.map((item) => {
                return { ...item, discountQty: 0, discountPercent: 100, paidQty: item.quantity };
            }),
            ruleDiscounts: [],
        };
    }

    const remainingQty: Record<string, number> = {};

    for (const item of items) {
        remainingQty[item.uuid] = (remainingQty[item.uuid] ?? 0) + item.quantity;
    }

    const ruleDiscounts: RuleDiscount[] = [];
    const discountQtyByUuid: Record<string, number> = {};
    const discountPercentByUuid: Record<string, number> = {};

    for (const rule of rules) {
        const discountPercent = rule.discount_percent ?? 100;

        const matchingItems = items.filter((item) => {
            return (remainingQty[item.uuid] ?? 0) > 0
                && itemMatchesRule(item, rule);
        });

        if (matchingItems.length === 0) {
            continue;
        }

        const matchedQty = matchingItems.reduce((sum, item) => {
            return sum + (remainingQty[item.uuid] ?? 0);
        }, 0);
        const discountCount = Math.floor(matchedQty / (rule.buy_quantity + rule.discount_quantity)) * rule.discount_quantity;

        if (discountCount === 0) {
            continue;
        }

        const sortedItems = [
            ...matchingItems,
        ].sort((a, b) => {
            return a.price - b.price;
        });
        let remaining = discountCount;
        const ruleDiscountItems: RuleDiscountItem[] = [];

        for (const item of sortedItems) {
            if (remaining <= 0) {
                break;
            }

            const available = remainingQty[item.uuid] ?? 0;
            const discountQty = Math.min(remaining, available);

            discountQtyByUuid[item.uuid] = (discountQtyByUuid[item.uuid] ?? 0) + discountQty;
            discountPercentByUuid[item.uuid] = discountPercent;
            ruleDiscountItems.push({
                uuid: item.uuid,
                name: item.name,
                sku: item.sku,
                quantity: discountQty,
                unitPrice: item.price,
                discountPercent,
            });
            remaining -= discountQty;
            remainingQty[item.uuid] -= discountQty;
        }

        for (const item of matchingItems) {
            remainingQty[item.uuid] = 0;
        }

        const savingAmount = ruleDiscountItems.reduce((sum, fi) => {
            return sum + (fi.quantity * fi.unitPrice * (fi.discountPercent / 100));
        }, 0);

        ruleDiscounts.push({ rule, discountItems: ruleDiscountItems, savingAmount });
    }

    const displayItems: DisplayItem[] = [];

    for (const item of items) {
        const discountQty = discountQtyByUuid[item.uuid] ?? 0;
        const discountPercent = discountPercentByUuid[item.uuid] ?? 100;

        displayItems.push({ ...item, discountQty, discountPercent, paidQty: item.quantity - discountQty });
    }

    return { displayItems, ruleDiscounts };
};

export interface ThresholdConfig {
    threshold: number;
    [key: string]: unknown;
}

export interface CalculateResult<T extends ThresholdConfig> {
    item: T | null;
    nextItem: T | null;
    remaining?: number | null;
}

export const calculate = <T extends ThresholdConfig>(subTotal: number, configuration: T[] | null | undefined): CalculateResult<T> => {
    if (!configuration) {
        return {
            item: null,
            nextItem: null,
        };
    }

    const items = configuration.toSorted((a, b) => {
        return b.threshold - a.threshold;
    });

    const currentItem = items.find((item) => {
        return subTotal > item.threshold;
    });

    const nextItem = items.reverse().find((item) => {
        return subTotal <= item.threshold;
    });

    return {
        item: currentItem || null,
        nextItem: nextItem || null,
        remaining: nextItem ? Math.max(nextItem.threshold - subTotal, 0.01) : null,
    };
};

export interface AdditionalDiscount {
    rule: DiscountRule;
    percent: number;
    amount: number;
}

/**
 * Order-total rules stack on top of the company discount ladder. The
 * threshold comparison is strictly `>` — identical to the server
 * (RecalculateQuoteTotalsAction) and the company configuration ladders.
 */
export const calculateAdditionalDiscounts = (netSubtotal: number, rules: DiscountRule[] | null | undefined): AdditionalDiscount[] => {
    return (rules ?? [])
        .filter((rule) => {
            return rule.type === 'order_total_percent'
                && netSubtotal > Number(rule.min_order_total ?? 0);
        })
        .map((rule) => {
            const percent = rule.discount_percent ?? 0;

            return { rule, percent, amount: netSubtotal * (percent / 100) };
        });
};

/**
 * The fields the company-discount helpers need; cart items carry the rule
 * discount fields optionally.
 */
export type DiscountableLineItem = Pick<QuoteLineItem, 'type' | 'tax_class' | 'price' | 'quantity'> & {
    discountQty?: number;
    discountPercent?: number;
};

/**
 * Mirrors `CompanyDiscountHelper::appliesTo()`: an empty or missing list of
 * product types means the company discount applies to every product.
 */
export const companyDiscountAppliesTo = (item: Pick<QuoteLineItem, 'type'>, discountProductTypes: string[] | null | undefined): boolean => {
    if (!discountProductTypes || discountProductTypes.length === 0) {
        return true;
    }

    return !!item.type && discountProductTypes.includes(item.type);
};

/**
 * Net line total after buy-X-get-Y rule discounts.
 */
export const netLineTotal = (item: DiscountableLineItem): number => {
    const discountQty = item.discountQty ?? 0;
    const discountPercent = item.discountPercent ?? 100;

    return (item.price * item.quantity) - (discountQty * item.price * (discountPercent / 100));
};

/**
 * Net subtotal of the items the company discount applies to. Both the
 * discount threshold and the discount amount are based on this amount,
 * matching `RecalculateQuoteTotalsAction`.
 */
export const discountableSubtotal = (items: DiscountableLineItem[], discountProductTypes: string[] | null | undefined): number => {
    return items.reduce((sum, item) => {
        return companyDiscountAppliesTo(item, discountProductTypes) ? sum + netLineTotal(item) : sum;
    }, 0);
};

/**
 * Taxable amount per tax class after the company discount (only on eligible
 * items) and the order-total discount (on every item).
 */
export const discountedTaxableAmountsByClass = (
    items: DiscountableLineItem[],
    discountProductTypes: string[] | null | undefined,
    companyDiscountPercent: number,
    additionalDiscountPercent: number
): Record<string, number> => {
    return items.reduce<Record<string, number>>((acc, item) => {
        const taxClass = item.tax_class ?? 'unspecified';
        const itemCompanyPercent = companyDiscountAppliesTo(item, discountProductTypes) ? companyDiscountPercent : 0;
        const factor = (100 - itemCompanyPercent - additionalDiscountPercent) / 100;

        acc[taxClass] = (acc[taxClass] ?? 0) + (netLineTotal(item) * factor);
        return acc;
    }, {});
};
